import { Link } from 'react-router-dom';
import { ArrowRight, Mic, ShieldCheck, Waves } from 'lucide-react';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';
import type { SiteSettings } from '@/lib/supabase/types';
import { Button } from '@/components/ui/button';
import { localeField, localePath } from '@/lib/i18n/helpers';
import { AnimatedWaveform } from '@/components/site/animated-waveform';
import { Counter } from '@/components/site/counter';

export function HeroSection({
  locale,
  dict,
  settings,
}: {
  locale: Locale;
  dict: Dictionary;
  settings: SiteSettings | null;
}) {
  const tagline = settings ? localeField(settings, 'tagline', locale) : '';

  return (
    <section className="hero-gradient relative isolate overflow-hidden text-white">
      {/* Floating gradient orbs — purely decorative, sit behind content. */}
      <div className="orb left-[-10%] top-[-15%] h-[28rem] w-[28rem] bg-accent/30" />
      <div
        className="orb right-[-12%] top-[10%] h-[22rem] w-[22rem] bg-primary/40"
        style={{ animationDelay: '4s' }}
      />
      <div
        className="orb left-[20%] bottom-[-30%] h-[26rem] w-[26rem] bg-accent/20"
        style={{ animationDelay: '8s' }}
      />

      {/* Subtle dot grid overlay */}
      <div className="dot-grid absolute inset-0 opacity-50" aria-hidden="true" />

      <div className="container-tight relative grid gap-10 pb-16 pt-12 sm:gap-14 sm:pb-20 sm:pt-16 md:pb-28 md:pt-20 lg:grid-cols-[1.1fr_1fr] lg:gap-12 lg:pb-32 lg:pt-24">
        <div className="animate-fade-up">
          <span className="relative inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/85 backdrop-blur-sm sm:text-xs">
            <span className="relative inline-flex h-2 w-2 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full border border-accent" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            <Mic className="h-3.5 w-3.5 text-accent" aria-hidden="true" />
            {dict.hero.eyebrow}
          </span>

          <h1 className="mt-5 font-serif text-3xl font-bold leading-[1.05] text-white sm:mt-6 sm:text-4xl md:text-5xl lg:text-6xl">
            {dict.hero.title.split(' ').map((word, i, arr) =>
              i === arr.length - 1 ? (
                <span key={i} className="text-gradient">{word}</span>
              ) : (
                <span key={i}>{word} </span>
              )
            )}
          </h1>

          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/75 sm:mt-6 sm:text-lg">
            {tagline || dict.hero.subtitle}
          </p>

          <div className="mt-5 flex flex-wrap gap-2 sm:mt-6">
            {[dict.hero.tag1, dict.hero.tag2, dict.hero.tag3].map((tag, i) => (
              <span
                key={tag}
                className="rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-medium text-accent/95 transition-all duration-300 hover:-translate-y-0.5 hover:border-accent/60 hover:bg-accent/20"
                style={{ animation: `fade-up 0.6s ease-out ${0.15 + i * 0.08}s both` }}
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3 sm:mt-8">
            <Button asChild size="lg" className="btn-gradient text-white shadow-lg shadow-accent/30 hover:shadow-accent/50">
              <Link to={localePath('/solicitar', locale)}>
                {dict.hero.ctaPrimary}
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <Link to={`/${locale}/areas`}>{dict.hero.ctaSecondary}</Link>
            </Button>
          </div>

          <dl className="mt-10 grid max-w-lg grid-cols-3 gap-4 border-t border-white/15 pt-7 sm:mt-12 sm:gap-6 sm:pt-8">
            <Stat
              value={String(settings?.years_experience ?? '15+')}
              label={dict.hero.stats.years}
            />
            <Stat
              value={String(settings?.periciales_count ?? '200+')}
              label={dict.hero.stats.cases}
            />
            <Stat
              value={String(settings?.areas_count ?? '4')}
              label={dict.hero.stats.areas}
            />
          </dl>
        </div>

        <div className="relative hidden lg:block">
          <div className="absolute inset-0 -z-10 spectrogram-bg opacity-30 blur-sm" aria-hidden="true" />

          <div className="relative animate-fade-up" style={{ animationDelay: '0.15s' }}>
            <AnimatedWaveform />
          </div>

          <div
            className="absolute -bottom-6 -left-6 flex items-center gap-3 rounded-xl border border-white/15 bg-[hsl(224,52%,16%)]/95 p-4 shadow-xl backdrop-blur-sm animate-float"
            style={{ animationDelay: '1s' }}
          >
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-accent/20 text-accent">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="text-xs">
              <p className="font-semibold text-white">{dict.hero.badge1Title}</p>
              <p className="text-white/60">{dict.hero.badge1Sub}</p>
            </div>
          </div>

          <div
            className="absolute -top-4 -right-4 flex items-center gap-3 rounded-xl border border-white/15 bg-[hsl(224,52%,16%)]/95 p-4 shadow-xl backdrop-blur-sm animate-float"
            style={{ animationDelay: '2.5s' }}
          >
            <span className="sound-bars" aria-hidden="true">
              <span style={{ height: '40%' }} />
              <span style={{ height: '70%' }} />
              <span style={{ height: '95%' }} />
              <span style={{ height: '60%' }} />
              <span style={{ height: '85%' }} />
            </span>
            <div className="text-xs">
              <p className="font-semibold text-white">{dict.hero.badge2Title}</p>
              <p className="text-white/60">{dict.hero.badge2Sub}</p>
            </div>
          </div>
        </div>
      </div>

      {/* SVG curve transition into next section */}
      <svg
        className="absolute bottom-0 left-0 right-0 h-12 w-full text-background"
        viewBox="0 0 1440 60"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M0,32 C320,72 720,8 1080,32 C1260,44 1380,52 1440,40 L1440,60 L0,60 Z"
          fill="currentColor"
        />
      </svg>
    </section>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <dt className="font-serif text-2xl font-bold text-white sm:text-3xl md:text-4xl">
        <Counter value={value} />
      </dt>
      <dd className="mt-1 text-[10px] uppercase tracking-wider text-white/60 sm:text-xs">{label}</dd>
    </div>
  );
}
